// src/components/properties/PropertySettingsTab.js

import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { Clock, Coffee, Save, Loader2 } from 'lucide-react';

const PropertySettingsTab = ({ property }) => {
    
    // Initialize state robustly (safely handles MySQL 1/0 or booleans)
    const [settings, setSettings] = useState({
        is_overtime_enabled: Number(property?.settings_overtime_enabled) === 1 || property?.settings_overtime_enabled === true,
        overtime_threshold: parseFloat(property?.settings_overtime_threshold) || 8.00,
        auto_meal_enabled: Number(property?.settings_auto_meal_enabled) === 1 || property?.settings_auto_meal_enabled === true,
        auto_meal_threshold: parseFloat(property?.settings_auto_meal_threshold) || 6.00,
        auto_meal_minutes: parseInt(property?.settings_auto_meal_minutes) || 30
    });
    
    const [savingSection, setSavingSection] = useState(null);

    // ONLY re-sync if the user clicks a completely different property.
    // This prevents the parent's stale cache from overwriting our correct local state.
    useEffect(() => {
        if (property) {
            setSettings({
                is_overtime_enabled: Number(property.settings_overtime_enabled) === 1 || property.settings_overtime_enabled === true,
                overtime_threshold: parseFloat(property.settings_overtime_threshold) || 8.00,
                auto_meal_enabled: Number(property.settings_auto_meal_enabled) === 1 || property.settings_auto_meal_enabled === true,
                auto_meal_threshold: parseFloat(property.settings_auto_meal_threshold) || 6.00,
                auto_meal_minutes: parseInt(property.settings_auto_meal_minutes) || 30
            });
        }
    }, [property?.id]);

    const handleSave = async (sectionName, overrideSettings = null) => {
        setSavingSection(sectionName);
        const dataToSave = overrideSettings || settings; 
        try {
            await api.updatePropertySettings(property.id, dataToSave);
            
            // "Cache Patch": Quietly update the parent's object reference in memory.
            // This ensures if the parent re-renders, it uses our newly saved data 
            // instead of reverting the UI back to the old database state.
            property.settings_overtime_enabled = dataToSave.is_overtime_enabled ? 1 : 0;
            property.settings_overtime_threshold = dataToSave.overtime_threshold;
            property.settings_auto_meal_enabled = dataToSave.auto_meal_enabled ? 1 : 0;
            property.settings_auto_meal_threshold = dataToSave.auto_meal_threshold;
            property.settings_auto_meal_minutes = dataToSave.auto_meal_minutes;

            toast.success(`${sectionName === 'overtime' ? 'Overtime' : 'Meal Deduction'} rules saved!`);
        } catch (err) {
            toast.error("Failed to save rules.");
        } finally {
            setSavingSection(null);
        }
    };

    // --- SMART TOGGLES ---
    const handleToggleOvertime = (e) => {
        const isChecked = e.target.checked;
        const newSettings = { ...settings, is_overtime_enabled: isChecked };
        setSettings(newSettings);
        if (!isChecked) handleSave('overtime', newSettings);
    };

    const handleToggleMeal = (e) => {
        const isChecked = e.target.checked;
        const newSettings = { ...settings, auto_meal_enabled: isChecked };
        setSettings(newSettings);
        if (!isChecked) handleSave('meal', newSettings);
    };

    // --- VALIDATION LOGIC ---
    const isOvertimeValid = settings.overtime_threshold && String(settings.overtime_threshold).trim() !== '' && parseFloat(settings.overtime_threshold) > 0;
    const isMealValid = settings.auto_meal_threshold && String(settings.auto_meal_threshold).trim() !== '' && parseFloat(settings.auto_meal_threshold) > 0 &&
                        settings.auto_meal_minutes && String(settings.auto_meal_minutes).trim() !== '' && parseInt(settings.auto_meal_minutes) > 0;

    if (!property) return null;

    return (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '1.2rem', color: '#0f172a' }}>Payroll & Automation Rules</h3>
            
            {/* --- OVERTIME SETTINGS --- */}
            <div style={{ border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', marginBottom: '20px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Clock size={22} color="#1798c1" />
                        <div>
                            <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b' }}>Overtime Rules</strong>
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Calculate overtime in payroll reports</span>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* ONLY VISIBLE WHEN TOGGLE IS ON */}
                        {settings.is_overtime_enabled && (
                            <button 
                                onClick={() => handleSave('overtime')} 
                                disabled={savingSection === 'overtime' || !isOvertimeValid}
                                title="Save Overtime Rules"
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    width: '36px', height: '36px', borderRadius: '8px', 
                                    border: 'none',
                                    backgroundColor: isOvertimeValid ? '#1798c1' : '#cbd5e1', 
                                    color: 'white', 
                                    cursor: isOvertimeValid ? 'pointer' : 'not-allowed',
                                    boxShadow: isOvertimeValid ? '0 2px 4px rgba(23, 152, 193, 0.3)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {savingSection === 'overtime' ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                            </button>
                        )}
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={settings.is_overtime_enabled} 
                                onChange={handleToggleOvertime} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>
                
                {settings.is_overtime_enabled && (
                    <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            Daily Overtime Threshold (Hours)
                        </label>
                        <input 
                            type="number" 
                            step="0.5"
                            className="form-input" 
                            style={{ width: '150px', background: 'white', borderColor: !isOvertimeValid ? '#ef4444' : '#e2e8f0' }}
                            value={settings.overtime_threshold} 
                            onChange={(e) => setSettings({...settings, overtime_threshold: e.target.value})}
                        />
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '8px', marginBottom: 0 }}>
                            Hours worked beyond this threshold in a single calendar day will be marked as overtime (OT).
                        </p>
                    </div>
                )}
            </div>

            {/* --- MEAL DEDUCTION SETTINGS --- */}
            <div style={{ border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Coffee size={22} color="#d97706" />
                        <div>
                            <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b' }}>Auto-Meal Deduction</strong>
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Automatically subtract unpaid lunch breaks</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* ONLY VISIBLE WHEN TOGGLE IS ON */}
                        {settings.auto_meal_enabled && (
                            <button 
                                onClick={() => handleSave('meal')} 
                                disabled={savingSection === 'meal' || !isMealValid}
                                title="Save Meal Rules"
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    width: '36px', height: '36px', borderRadius: '8px', 
                                    border: 'none',
                                    backgroundColor: isMealValid ? '#1798c1' : '#cbd5e1', 
                                    color: 'white', 
                                    cursor: isMealValid ? 'pointer' : 'not-allowed',
                                    boxShadow: isMealValid ? '0 2px 4px rgba(23, 152, 193, 0.3)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {savingSection === 'meal' ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                            </button>
                        )}
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={settings.auto_meal_enabled} 
                                onChange={handleToggleMeal} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>

                {settings.auto_meal_enabled && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                Minimum Shift Length (Hours)
                            </label>
                            <input 
                                type="number" 
                                step="0.5"
                                className="form-input" 
                                style={{ width: '180px', background: 'white', borderColor: !isMealValid ? '#ef4444' : '#e2e8f0' }}
                                value={settings.auto_meal_threshold} 
                                onChange={(e) => setSettings({...settings, auto_meal_threshold: e.target.value})}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                Minutes to Deduct
                            </label>
                            <input 
                                type="number" 
                                step="5"
                                className="form-input" 
                                style={{ width: '180px', background: 'white', borderColor: !isMealValid ? '#ef4444' : '#e2e8f0' }}
                                value={settings.auto_meal_minutes} 
                                onChange={(e) => setSettings({...settings, auto_meal_minutes: e.target.value})}
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <p style={{ fontSize: '0.85rem', color: '#1798c1', margin: 0, backgroundColor: '#f0f9ff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                <strong>Example:</strong> If a shift is longer than <strong>{settings.auto_meal_threshold || 'X'} hours</strong>, the system will automatically subtract <strong>{settings.auto_meal_minutes || 'Y'} minutes</strong> from their total paid time.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PropertySettingsTab;